/**
 * 七牛云上传 Vue3 插件
 * 上传凭证由后端签发，前端不存放 AccessKey / SecretKey
 */
import { getPrivateDownloadUrl, getUploadToken } from '@/api/qiniu'

const QiniuUploader = {
  install(app, options = {}) {
    const defaultConfig = {
      /** 可选：后端未返回 domain 时用于拼接公开访问 URL（非密钥） */
      domain: '',
      timeout: 30000,
      prefix: '/admin',
    }

    const config = { ...defaultConfig, ...options }

    function getFileTypeByExtension(ext) {
      if (!ext) return 'file'
      const lowerExt = ext.toLowerCase()
      const imageExts = [
        '.jpg',
        '.jpeg',
        '.png',
        '.gif',
        '.bmp',
        '.webp',
        '.svg',
        '.pdf',
        '.mp4',
        '.avi',
        '.mov',
        '.wmv',
        '.flv',
        '.webm',
        '.mkv',
        '.m4v',
      ]
      if (imageExts.includes(lowerExt)) {
        return 'image'
      }
      return 'file'
    }

    function generateRandomFileName(ext = '', fileType = 'file', customName) {
      const validTypes = ['file', 'image']
      if (!validTypes.includes(fileType)) {
        fileType = 'file'
      }
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      const datePath = `${year}-${month}-${day}`
      const randomName = `${year}${month}${day}${Date.now()}`
      let fileName = `mini/${fileType}/${datePath}/${randomName}${ext.toLowerCase()}`
      if (customName) {
        fileName = `mini/${fileType}/${customName}/${datePath}/${randomName}${ext.toLowerCase()}`
      }
      return encodeURI(fileName)
    }

    /**
     * 与后端一致：
     * data: { token, expires_in, bucket, key }，可选 domain / cdn_domain / base_url
     */
    function pickUploadTokenResponse(res) {
      if (!res || res.code !== 200) return null
      const d = res.data
      if (typeof d === 'string') {
        return {
          token: d,
          expires_in: null,
          bucket: null,
          key: null,
          domain: null,
        }
      }
      if (d && typeof d === 'object') {
        const token =
          d.token || d.upload_token || d.uptoken || ''
        if (!token) return null
        return {
          token,
          expires_in:
            d.expires_in !== undefined && d.expires_in !== null
              ? d.expires_in
              : null,
          bucket:
            d.bucket !== undefined && d.bucket !== null ? d.bucket : null,
          key: d.key !== undefined && d.key !== null ? d.key : null,
          domain:
            d.domain || d.cdn_domain || d.base_url || null,
        }
      }
      return null
    }

    function pickPrivateDownloadUrl(res) {
      if (!res || res.code !== 200) return ''
      const d = res.data
      if (typeof d === 'string') return d
      if (d && typeof d === 'object') {
        return (
          d.url ||
          d.download_url ||
          d.private_download_url ||
          d.file_url ||
          d.signed_url ||
          ''
        )
      }
      return ''
    }

    /**
     * 从完整 URL 或已是 object key 的字符串解析出 fileKey（供私有下载签名接口使用）
     */
    function resolveFileKey(baseUrlOrKey) {
      if (!baseUrlOrKey) return ''
      const s = String(baseUrlOrKey).trim()
      if (!/^https?:\/\//i.test(s)) {
        return s.replace(/^\//, '')
      }
      try {
        const u = new URL(s)
        return u.pathname.replace(/^\//, '')
      } catch {
        return s.replace(/^\//, '')
      }
    }

    /**
     * 向后兼容：原先用 AK/SK 算私有链，现改为请求后端
     */
    async function generateSignedUrl(baseUrl, expireSeconds = 3600) {
      const fileKey = resolveFileKey(baseUrl)
      if (!fileKey) {
        throw new Error('无效的文件路径')
      }
      const res = await getPrivateDownloadUrl({
        fileKey,
        expires: expireSeconds,
      })
      const url = pickPrivateDownloadUrl(res)
      if (!url) {
        throw new Error('获取访问地址失败')
      }
      return url
    }

    async function fetchUploadToken(key) {
      const res = await getUploadToken({ key })
      const picked = pickUploadTokenResponse(res)
      if (!picked || !picked.token) {
        console.error('获取七牛上传凭证失败', res)
        return null
      }
      return picked
    }

    async function uploadFile(file, options = {}, progress, customName) {
      if (!window.qiniu) {
        console.error(
          '请先引入七牛SDK：https://cdn.jsdelivr.net/npm/qiniu-js@3.4.0/dist/qiniu.min.js'
        )
        return Promise.reject(new Error('七牛SDK未加载'))
      }

      const ext =
        file.name.lastIndexOf('.') > -1
          ? file.name.substring(file.name.lastIndexOf('.'))
          : ''
      const fileType = getFileTypeByExtension(ext)
      const { key: keyOption, region } = options
      const keyFromClient = keyOption || generateRandomFileName(ext, fileType, customName)

      const credential = await fetchUploadToken(keyFromClient)
      if (!credential) {
        return Promise.reject(new Error('获取上传凭证失败'))
      }
      const { token, domain: tokenDomain, key: keyFromServer } = credential
      const uploadKey =
        keyFromServer !== undefined && keyFromServer !== null && keyFromServer !== ''
          ? keyFromServer
          : keyFromClient
      const uploadDomain = (
        tokenDomain ||
        config.domain ||
        ''
      ).replace(/\/$/, '')

      const uploadOptions = {
        useCdnDomain: true,
        region: region ?? null,
        timeout: config.timeout,
      }

      const observable = window.qiniu.upload(
        file,
        uploadKey,
        token,
        {},
        uploadOptions
      )

      return new Promise((resolve, reject) => {
        observable.subscribe({
          next(res) {
            if (progress && typeof progress === 'function') {
              progress(res.total.percent)
            }
          },
          error(err) {
            console.error('上传失败', err)
            reject(err)
          },
          complete(res) {
            const publicUrl = uploadDomain
              ? decodeURIComponent(`${uploadDomain}/${res.key}`)
              : res.key
            resolve({
              ...res,
              url: publicUrl,
            })
          },
        })
      })
    }

    async function uploadAndGetSignedUrl(
      file,
      customName,
      expireSeconds = 3600
    ) {
      const uploadResult = await uploadFile(
        file,
        {},
        () => {},
        customName
      )
      const signedUrl = await generateSignedUrl(
        uploadResult.key || uploadResult.url,
        expireSeconds
      )
      return {
        short: uploadResult.key,
        full_path: signedUrl,
      }
    }

    /**
     * 兼容旧调用：改为向后端申请上传 token（需传 key）
     */
    async function generateToken(options = {}) {
      let key = options.key
      if (!key && options.scope && typeof options.scope === 'string') {
        const parts = options.scope.split(':')
        key = parts.length > 1 ? parts.slice(1).join(':') : options.scope
      }
      if (!key) {
        console.error('generateToken 需要提供 key 或 bucket:key 形式的 scope')
        return null
      }
      const credential = await fetchUploadToken(key)
      return credential ? credential.token : null
    }

    app.config.globalProperties.$qiniu = {
      setConfig(newConfig) {
        Object.assign(config, newConfig)
      },
      getConfig() {
        return { ...config }
      },
      generateToken,
      uploadFile,
      generateSignedUrl,
      uploadAndGetSignedUrl,
    }

    app.provide('qiniu', {
      setConfig: (newConfig) => Object.assign(config, newConfig),
      getConfig: () => ({ ...config }),
      generateToken,
      uploadFile,
      generateSignedUrl,
      uploadAndGetSignedUrl,
    })
  },
}

export default QiniuUploader
